import { Link } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardCheck,
  faFileCircleQuestion,
  faMagnifyingGlass,
  faPenToSquare,
  faUserCheck
} from "@fortawesome/free-solid-svg-icons";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../seo/site";

const STEPS = [
  {
    icon: faMagnifyingGlass,
    title: "Search by Venture Name or Location",
    text: "Start your property research with a simple search. Enter a venture or project name, village, locality, city, mandal, district, or developer name where searchable. Matching venture records are displayed based on available information.",
    points: [
      "Venture or project name",
      "Village, locality, or area",
      "City, mandal, or district",
      "Developer or builder name, where searchable"
    ]
  },
  {
    icon: faClipboardCheck,
    title: "Explore Available Venture Details",
    text: "Open a venture listing to review available project information. Details may vary by record and should be treated as references for further investigation.",
    points: [
      "Venture name and project category/status",
      "RERA registration number and related details",
      "HMDA, DTCP, or other planning authority references",
      "LP / plan approval and application numbers",
      "Developer / promoter and location details",
      "Photos, layout plans, and documents where available"
    ]
  },
  {
    icon: faFileCircleQuestion,
    title: "Need More Information? Raise a Request",
    text: "Submit an information request when a listing does not answer your questions. Our team reviews requests and assists where information is available and accessible.",
    points: [
      "Available RERA registration certificates or related records",
      "Additional layout or plan approval references",
      "Further venture and developer information",
      "Available project photographs or layout plans",
      "Clarification of listed project details",
      "Help identifying the relevant official source for verification"
    ]
  },
  {
    icon: faUserCheck,
    title: "Venture Owners Can Request Updates",
    text: "Venture owners, developers, builders, and authorized representatives can register and request corrections or updates to improve listing accuracy.",
    points: [
      "Venture or project name and developer details",
      "Project location and address",
      "RERA, HMDA / DTCP, and LP references",
      "Project descriptions and specifications",
      "Contact details and website information",
      "Photographs, layout plans, maps, and supporting documents"
    ]
  },
  {
    icon: faPenToSquare,
    title: "Our Team Reviews and Approves Updates",
    text: "Submitted changes are reviewed for usefulness and reliability. We may request clarification or additional documentation before publishing accepted updates.",
    points: [
      "Review of requested updates and supporting information",
      "Possible request for authorization evidence",
      "Publication of accepted changes to the listing",
      "Directory-content review only—not legal certification"
    ]
  }
];

const START_OPTIONS = [
  {
    title: "For Property Buyers and Investors",
    text: "Search ventures, explore available registration and approval details, and request additional information to support your property research.",
    cta: "Search Ventures",
    to: "/"
  },
  {
    title: "For Anyone Who Needs More Information",
    text: "Request help finding available certificates, plan references, documents, or other venture-related details.",
    cta: "Contact Us",
    to: "/contact"
  },
  {
    title: "For Venture Owners and Developers",
    text: "Register your venture, report missing or incorrect information, and submit supporting photographs, plans, and documents for review.",
    cta: "Contact Us",
    to: "/contact"
  }
];

const FAQS = [
  {
    q: "How do I search for a venture on VentureDirectory.com?",
    a: "Enter the venture name or location in the search field. Browse the matching results and open a listing to view the information available for that project."
  },
  {
    q: "Can I check RERA and HMDA/DTCP approval details?",
    a: "You can explore available RERA registration numbers, planning authority references, layout approval numbers, and other listed details. Confirm their validity and current status with the relevant official authority before proceeding with a purchase."
  },
  {
    q: "How can I get a RERA certificate or additional venture information?",
    a: "Submit a request identifying the venture and the information or document you need. Our team will review your request and assist where the relevant information is available and accessible."
  },
  {
    q: "What should I do if a venture listing contains incorrect information?",
    a: "Register or contact our team and submit the correct details with supporting documents or evidence. Your proposed changes will be reviewed before any approved update is published."
  },
  {
    q: "Can a developer add photographs and layout plans?",
    a: "Yes. Venture owners, developers, and authorized representatives can submit relevant photographs, layout plans, maps, and supporting documents for review. Accepted materials may be added to the listing after approval."
  },
  {
    q: "Who can request changes to a venture listing?",
    a: "Venture owners, developers, builders, and authorized representatives may request changes. Our team may require evidence of identity, authorization, or a legitimate connection with the project before approving an update."
  },
  {
    q: "Are all venture details independently verified?",
    a: "Information availability and verification status may vary by record. A listing on VentureDirectory.com should not be treated as a guarantee of legal validity or official approval. Users should verify material information directly with the relevant authorities and seek independent legal advice when necessary."
  },
  {
    q: "Does VentureDirectory.com charge for information requests or updates?",
    a: "Please contact our team or check the applicable service terms for any charges associated with document assistance, premium services, or promotional listings."
  }
];

function HowItWorksPage() {
  return <main className="vd-page">
      <Seo {...PAGE_SEO.howItWorks} />
      <section className="vd-page-hero">
        <div className="vd-page-hero-inner">
          <p className="vd-page-kicker">How It Works</p>
          <h1>Search, Verify &amp; Update Venture Details</h1>
          <p className="vd-page-lead">
            Search a Venture. Discover Key Details. Request More Information. Keep Venture Records Updated.
          </p>
          <p>
            Finding essential information about a real estate venture should not be complicated.
            VentureDirectory.com helps you find available venture information through a simple search—
            whether you are buying an open plot, researching a residential layout, evaluating an investment,
            or checking approval details.
          </p>
        </div>
      </section>

      <section className="vd-page-section">
        <div className="vd-page-section-head">
          <h2>How VentureDirectory.com Works</h2>
          <p>Five clear steps from search to reviewed listing updates.</p>
        </div>
        <div className="vd-page-steps">
          {STEPS.map((step, index) => <article key={step.title} className="vd-page-step">
              <div className="vd-page-step-badge">
                <span>Step {index + 1}</span>
                <FontAwesomeIcon icon={step.icon} aria-hidden="true" />
              </div>
              <div className="vd-page-step-body">
                <h3>{step.title}</h3>
                <p>{step.text}</p>
                <ul className="vd-page-list">
                  {step.points.map((point) => <li key={point}>{point}</li>)}
                </ul>
              </div>
            </article>)}
        </div>
        <p className="vd-page-callout">
          Important: A displayed registration or approval number is a reference for further investigation,
          not proof by itself that a project is currently approved or legally compliant.
        </p>
      </section>

      <section className="vd-page-section">
        <div className="vd-page-section-head">
          <h2>One Platform. Three Simple Ways to Get Started.</h2>
          <p>Choose the path that matches what you need today.</p>
        </div>
        <div className="vd-page-audience-grid">
          {START_OPTIONS.map((item) => <article key={item.title} className="vd-page-audience">
              <h3>{item.title}</h3>
              <p>{item.text}</p>
              <Link to={item.to} className="vd-page-cta">{item.cta}</Link>
            </article>)}
        </div>
      </section>

      <section className="vd-page-section">
        <div className="vd-page-card">
          <h2>Why Keep Venture Details Updated?</h2>
          <p>
            Accurate, complete, and discoverable project information can help property buyers and real estate
            professionals research ventures more efficiently.
          </p>
          <ul className="vd-page-list">
            <li>For developers, an updated listing can improve online discoverability and help prospective buyers access the latest information supplied for the project.</li>
            <li>For buyers, more complete listings can make it easier to identify relevant questions, locate approval references, and pursue independent verification before making a property decision.</li>
            <li>For the real estate ecosystem, structured information can reduce avoidable confusion and encourage more informed conversations between buyers, developers, and property professionals.</li>
          </ul>
          <p className="vd-page-emphasis">Better information starts with better records.</p>
        </div>
      </section>

      <section className="vd-page-section">
        <div className="vd-page-section-head">
          <h2>Frequently Asked Questions</h2>
          <p>Quick answers to the most common questions about searching and updating ventures.</p>
        </div>
        <div className="vd-page-faq-list">
          {FAQS.map((item) => <details key={item.q} className="vd-page-faq">
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>)}
        </div>
      </section>

      <section className="vd-page-section">
        <div className="vd-page-notice">
          <h2>Start Exploring VentureDirectory.com Today</h2>
          <p>
            Whether you are researching your next property investment, looking for venture approval references,
            seeking additional project information, or updating your venture listing, VentureDirectory.com helps
            you take the next step.
          </p>
          <p className="vd-page-emphasis">Search. Discover. Request. Update. Build Trust.</p>
          <div className="vd-page-cta-row">
            <Link to="/" className="vd-page-cta">Search Ventures</Link>
            <Link to="/about" className="vd-page-cta ghost">About Us</Link>
            <Link to="/contact" className="vd-page-cta ghost">Contact Us</Link>
          </div>
          <p>
            Disclaimer: VentureDirectory.com is a real estate information and discovery platform. Listing information
            and submitted documents may be incomplete, outdated, or subject to change. Requests and updates are subject
            to review, and approval for publication does not constitute official government verification or legal certification.
            Always independently verify relevant approvals, registration status, ownership, title, and other material
            property details before making a transaction.
          </p>
        </div>
      </section>
    </main>;
}

export default HowItWorksPage;
